import type {SliceComponentProps} from '@prismicio/react'
import {useRouter} from 'next/router'
import type {EventDocument, EventsContainerSlice} from 'prismicio-types'
import type {FC} from 'react'
import {useCallback, useMemo, useRef, useState} from 'react'
import {ToastProvider} from 'react-toast-notifications'
// eslint-disable-next-line boundaries/dependencies -- grandfathered cross-slice import, predates the slice-boundaries rule
import {PaginationControls} from 'slices/BlogPostsContainer/components/PaginationControls'
import {Container} from 'src/common/Container'
import type {Context} from 'src/common/types'
import type {EventType} from 'src/common/utils/eventRoutes'
import {EVENT_TYPES, formatEventTypeDisplay} from 'src/common/utils/eventRoutes'
import {EventFilters} from 'src/Event/components/EventFilters'
import {EventsCarousel} from 'src/Event/components/EventsCarousel'
import {EventsGrid} from 'src/Event/components/EventsGrid'
import {PaginationSEO} from 'src/Event/components/PaginationSEO'
import {useEventLabels} from 'src/Event/hooks/useEventLabels'

export type EventsContainerProps = SliceComponentProps<EventsContainerSlice> & {
  context: Context
}

const EventsContainer: FC<EventsContainerProps> = ({context, slice}) => {
  const router = useRouter()
  const containerRef = useRef<HTMLDivElement>(null)
  const onDemandSectionRef = useRef<HTMLDivElement>(null)

  const baseUrl = `/${context.pageUid}`

  const currentPage = Number(router.query.page) || 1

  const eventsByType = useMemo(() => context.eventsByType || {}, [context.eventsByType])
  const eventTypes = useMemo(() => context.eventTypes || [], [context.eventTypes])
  const totalPages = context.totalPages || 0

  const eventLabels = useEventLabels({
    emptyState: slice.primary.labels_empty_state,
    filters: slice.primary.labels_filters,
    cards: slice.primary.labels_cards,
  })

  const [activeType, setActiveType] = useState<EventType | null>(null)

  const allEvents = useMemo(() => Object.values(eventsByType).flat(), [eventsByType])

  const [filteredEvents, setFilteredEvents] = useState<EventDocument<string>[]>(allEvents)

  const handleFilterChange = useCallback((filtered: EventDocument<string>[]) => {
    setFilteredEvents(filtered)
  }, [])

  const handleTypeChange = useCallback((type: EventType | null) => {
    setActiveType(type)
  }, [])

  const filteredEventsByType = useMemo(() => {
    const grouped: Record<string, EventDocument<string>[]> = {}

    filteredEvents.forEach((event) => {
      const type = event.data.event_type

      if (!grouped[type]) {
        grouped[type] = []
      }

      grouped[type].push(event)
    })

    return grouped
  }, [filteredEvents])

  const scrollToOnDemandSection = useCallback(() => {
    if (onDemandSectionRef.current) {
      const yOffset = -100 // Offset for fixed header
      const y = onDemandSectionRef.current.getBoundingClientRect().top + window.scrollY + yOffset
      window.scrollTo({top: y, behavior: 'smooth'})
    }
  }, [])

  const visibleEventTypes = useMemo(() => {
    if (activeType) {
      return [activeType]
    }

    return eventTypes
  }, [activeType, eventTypes])

  const renderEventSection = useCallback(
    (eventType: EventType) => {
      const events = filteredEventsByType[eventType] || []
      const hasEvents = events.length > 0
      const isOnDemand = eventType === EVENT_TYPES.ON_DEMAND

      // Hide entire section if no events
      if (!hasEvents) {
        return null
      }

      const sectionTitle = formatEventTypeDisplay(eventType)

      return (
        <div key={eventType} className="flex flex-col gap-6" ref={isOnDemand ? onDemandSectionRef : null}>
          <h2 className="text-24 font-medium leading-[30px] text-moss-black">{sectionTitle}</h2>

          {isOnDemand ? (
            <EventsGrid events={events} labels={eventLabels.cards} />
          ) : (
            <EventsCarousel events={events} labels={eventLabels.cards} />
          )}

          {isOnDemand && totalPages > 1 && (
            <PaginationControls
              totalPages={totalPages}
              currentPage={currentPage}
              baseUrl={baseUrl}
              onScroll={scrollToOnDemandSection}
            />
          )}
        </div>
      )
    },
    [filteredEventsByType, totalPages, currentPage, scrollToOnDemandSection, baseUrl, eventLabels.cards],
  )

  return (
    <ToastProvider autoDismiss={5000}>
      {totalPages > 1 && (
        <PaginationSEO currentPage={currentPage} totalPages={totalPages} baseUrl={baseUrl} locale={router.locale} />
      )}

      <Container className="!pb-0 !pt-1" backgroundColor="Grey/100">
        <div
          ref={containerRef}
          className="mx-auto flex w-full max-w-[1200px] flex-col gap-12 px-5 pb-12 md:px-10 lg:px-5 xl:px-0"
        >
          <EventFilters
            events={allEvents}
            eventTypes={eventTypes}
            activeType={activeType}
            onTypeChange={handleTypeChange}
            onFilterChange={handleFilterChange}
            labels={eventLabels.filters}
          />

          <div className="flex flex-col gap-16">
            {visibleEventTypes.map((eventType) => renderEventSection(eventType))}
          </div>
        </div>
      </Container>
    </ToastProvider>
  )
}

export default EventsContainer
